import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import capitalize from 'lodash/capitalize';

import convertSsnKey from './convertSsnKey';
import { SPACE } from '../../../constants/chars';
import { RULES } from '../../../utils/validations';
import transformSsnValue from './transformSsnValue';
import { getPhoneWithoutUSCode } from '../../../utils/getPhone';
import { LENGTH_VALUES } from '../../../constants/validations';
import convertFieldsError from '../Profile/convertFieldsError';
import { PROFILE_FIELDS } from '../../../constants/strings';
import { DOCUMENTATION_TYPES as DOCS, FIELDS_TYPE } from '../../../constants';

import {
  convertDateToAPIFormat,
  convertDateToUIFormat,
} from '../../../utils/converters/dateConverters';
import {
  convertGenderList,
  convertMaritalList,
  convertGenderIdentitiesList,
  convertListWithName,
} from '../../../utils/converters/convertListWithLocalization';

const MAX_FILE_SIZE = 32;
const getPersonalFields = (isComplex, config, formatMessage) => {
  const {
    dob,
    ssn,
    race,
    gender,
    errors,
    mStatus,
    lastName,
    language,
    firstName,
    languages,
    ethnicity,
    middleName,
    genderList,
    nameSuffix,
    rawRaceList,
    citizenship,
    languageList,
    veteranStatus,
    nameSuffixList,
    educationLevel,
    genderIdentity,
    profileEditable,
    rawEthnicityList,
    maritalStatusList,
    genderIdentityList,
    educationLevelsList,
    veteranStatusesList,
    primaryPatientProfile,
    ssnHidingEnabled,
  } = config;

  const raceFields = primaryPatientProfile
    ? [
        {
          name: formatMessage(PROFILE_FIELDS.fieldNames.race),
          value: race,
          type: 'select',
          options: rawRaceList,
          fieldName: 'race',
        },
        {
          name: formatMessage(PROFILE_FIELDS.fieldNames.ethnicity),
          value: ethnicity,
          type: 'select',
          options: rawEthnicityList,
          fieldName: 'ethnicity',
        },
      ]
    : [];

  const ssnField =
    primaryPatientProfile && !ssnHidingEnabled
      ? [
          {
            name: formatMessage(PROFILE_FIELDS.fieldNames.ssnLong),
            placeholder: formatMessage({ id: 'profile.fields.ssnPlaceHolder' }),
            value: convertSsnKey(ssn),
            fieldName: 'ssn',
            disabled: !profileEditable,
            limit: LENGTH_VALUES.SSN,
            shouldClearWhenOnFocus: true,
            validationRules: {
              [RULES.SSN]: {
                message: 'profile.ssn.format',
                expectedLengthShort: LENGTH_VALUES.SSN_LAST_4,
                expectedLengthFull: LENGTH_VALUES.SSN,
              },
            },
            ...(profileEditable && {
              type: FIELDS_TYPE.PASSWORD,
              transformValue: transformSsnValue,
            }),
          },
        ]
      : [];

  let nameFields = [
    {
      name: formatMessage(PROFILE_FIELDS.fieldNames.firstName),
      value: firstName,
      fieldName: 'firstName',
      validationRules: {
        [RULES.REQUIRED]: {
          message: 'profile.firstName.required',
        },
      },
      limit: LENGTH_VALUES.FIRST_NAME.MAX,
      disabled: !profileEditable,
    },
    {
      name: formatMessage(PROFILE_FIELDS.fieldNames.middleName),
      value: middleName,
      fieldName: 'middleName',
      limit: LENGTH_VALUES.MIDDLE_NAME.MAX,
      disabled: !profileEditable,
    },
    {
      name: formatMessage(PROFILE_FIELDS.fieldNames.lastName),
      value: lastName,
      fieldName: 'lastName',
      validationRules: {
        [RULES.REQUIRED]: {
          message: 'profile.lastName.required',
        },
      },
      limit: LENGTH_VALUES.LAST_NAME.MAX,
      disabled: !profileEditable,
    },
    {
      name: formatMessage(PROFILE_FIELDS.fieldNames.nameSuffix),
      value: nameSuffix,
      type: 'select',
      fieldName: 'nameSuffix',
      options: nameSuffixList,
      disabled: !profileEditable,
    },
  ];

  if (!isComplex) {
    nameFields = [
      {
        name: formatMessage(PROFILE_FIELDS.fieldNames.name),
        separator: SPACE,
        value: nameFields,
      },
    ];
  }

  let genderIdentityValue;

  if (get(genderIdentity, 'description')) {
    genderIdentityValue = get(genderIdentity, 'id');
  }

  const fields = [
    ...nameFields,
    {
      name: formatMessage(PROFILE_FIELDS.fieldNames.gender),
      value: gender,
      type: 'select',
      options: genderList,
      fieldName: 'gender',
    },
    {
      name: formatMessage(PROFILE_FIELDS.fieldNames.genderIdentity),
      value: genderIdentityValue,
      type: 'select',
      options: genderIdentityList,
      fieldName: 'genderIdentityId',
    },
    {
      name: formatMessage(PROFILE_FIELDS.fieldNames.dob),
      value: convertDateToUIFormat(dob, formatMessage),
      fieldName: 'dob',
      type: 'date',
      validationRules: {
        [RULES.DOB]: {
          message: 'profile.dob.format',
        },
        [RULES.REQUIRED]: {
          message: 'profile.dob.required',
        },
      },
      converters: {
        fromView: value => convertDateToAPIFormat(value, formatMessage),
      },
      withManualInputDob: true,
      disabled: !profileEditable,
    },
    ...ssnField,
    {
      name: formatMessage(PROFILE_FIELDS.fieldNames.mStatus),
      value: mStatus,
      type: 'select',
      options: maritalStatusList,
      fieldName: 'mStatus',
    },
    {
      name: formatMessage(PROFILE_FIELDS.fieldNames.prefLanguage),
      value: language,
      type: 'select',
      options: languageList,
      fieldName: 'language',
    },
    {
      name: formatMessage(PROFILE_FIELDS.fieldNames.languages),
      value: languages,
      type: 'select',
      multi: true,
      options: languageList,
      transformValueFromOptions: true,
      fieldName: 'languages',
    },
    ...raceFields,
    {
      name: formatMessage(PROFILE_FIELDS.fieldNames.veteranStatus),
      value: veteranStatus && veteranStatus.id,
      type: 'select',
      options: veteranStatusesList,
      transformValueFromOptions: true,
      fieldName: 'veteranStatusId',
    },
    {
      name: formatMessage(PROFILE_FIELDS.fieldNames.educationLevel),
      value: educationLevel && educationLevel.id,
      type: 'select',
      options: educationLevelsList,
      transformValueFromOptions: true,
      fieldName: 'educationLevelId',
    },
    {
      name: formatMessage(PROFILE_FIELDS.fieldNames.citizenship),
      value: citizenship,
      fieldName: 'citizenship',
      limit: LENGTH_VALUES.CITIZENSHIP,
    },
  ];

  return {
    title: formatMessage(PROFILE_FIELDS.listTitles.personal),
    errors: convertFieldsError(fields, errors),
    fields,
    columns: true,
  };
};

const getGeneralProfileFields = (
  {
    bio,
    healthInterests,
    aliasName,
    username,
    healthInterestsList,
    userOrganizations,
    hasActiveProfile,
    canEditOwnPatient,
    employeeProfile = {},
  },
  formatMessage,
) => {
  return [
    {
      name: formatMessage(PROFILE_FIELDS.fieldNames.username),
      value: username,
      fieldName: 'username',
      validationRules: {
        [RULES.REQUIRED]: {
          message: 'profile.username.required',
        },
        [RULES.USERNAME_FORMAT]: {
          message: 'profile.username.format',
        },
        [RULES.MIN_LENGTH]: {
          message: 'profile.username.minCharacters',
          length: LENGTH_VALUES.USERNAME.MIN,
        },
      },
      limit: LENGTH_VALUES.USERNAME.MAX,
      disabled: canEditOwnPatient,
      hintText: formatMessage(PROFILE_FIELDS.fieldHints.username),
    },
    {
      name: formatMessage(PROFILE_FIELDS.fieldNames.aliasName),
      value: aliasName,
      fieldName: 'aliasName',
      validationRules: {
        [RULES.REQUIRED]: {
          message: 'profile.aliasName.required',
        },
        [RULES.ALIAS_FORMAT]: {
          message: 'profile.aliasName.format',
        },
        [RULES.MIN_LENGTH]: {
          message: 'profile.aliasName.minCharacters',
          length: LENGTH_VALUES.ALIAS.MIN,
        },
      },
      limit: LENGTH_VALUES.ALIAS.MAX,
      disabled: canEditOwnPatient,
      hintText: formatMessage(PROFILE_FIELDS.fieldHints.aliasName),
    },
    {
      name: formatMessage(PROFILE_FIELDS.fieldNames.bio),
      value: bio,
      type: 'textarea',
      fieldName: 'bio',
      limit: LENGTH_VALUES.BIO.MAX,
      fullWidth: true,
    },
    {
      name: formatMessage(PROFILE_FIELDS.fieldNames.healthInterests),
      value: healthInterests,
      type: 'select',
      multi: true,
      options: healthInterestsList,
      fieldName: 'healthInterests',
      fullWidth: true,
    },
    {
      name: formatMessage(PROFILE_FIELDS.fieldNames.organizations),
      value: userOrganizations && userOrganizations.join(', '),
      type: 'textarea',
      fieldName: 'userOrganizations',
      limit: LENGTH_VALUES.ORGANIZATION.MAX,
      disabled: true,
      fullWidth: true,
    },
    ...(!isEmpty(employeeProfile) && [
      {
        name: formatMessage(PROFILE_FIELDS.listTitles.affiliations),
        value:
          employeeProfile &&
          employeeProfile.employeeEmployers
            .map(item => item.employer.name)
            .join(', '),
        type: 'textarea',
        fieldName: 'employers',
        disabled: true,
        fullWidth: true,
      },
    ]),
  ];
};

const getTeamProfileFields = ({ actingAsTeam }, formatMessage) => {
  return [
    {
      name: formatMessage(PROFILE_FIELDS.fieldNames.organizations),
      value: get(actingAsTeam, 'organization.name'),
      type: 'textarea',
      fieldName: 'organizations',
      fullWidth: true,
    },
    {
      name: formatMessage(PROFILE_FIELDS.fieldNames.description),
      value: get(actingAsTeam, 'description'),
      type: 'textarea',
      fieldName: 'description',
      fullWidth: true,
    },
  ];
};

export const getProfileFieldsetList = (
  teamActingUser,
  options,
  formatMessage,
) => {
  const { errors } = options;

  const fields = teamActingUser
    ? getTeamProfileFields(options, formatMessage)
    : getGeneralProfileFields(options, formatMessage);

  return [
    {
      title: formatMessage(PROFILE_FIELDS.listTitles.general),
      errors: convertFieldsError(fields, errors),
      fields,
      columns: true,
    },
  ];
};

export const getProviderFieldsetList = ({
  profile,
  rawGenderList,
  rawMaritalStatusList,
  languageList,
  canEdit,
  nameSuffixList,
  intl: { formatMessage },
  profileEditable,
  errors,
  veteranStatusesList,
  educationLevelsList,
}) => {
  const { mStatus } = profile;

  const genderList = convertGenderList(rawGenderList, formatMessage);
  const maritalStatusList = convertMaritalList(
    rawMaritalStatusList,
    formatMessage,
  );
  const updatedVeteranStatusesList = convertListWithName(veteranStatusesList);
  const updatedEducationLevelsList = convertListWithName(educationLevelsList);

  const personalConfig = {
    ...profile,
    mStatus,
    profileEditable,
    genderList,
    maritalStatusList,
    nameSuffixList,
    languageList,
    errors,
    veteranStatusesList: updatedVeteranStatusesList,
    educationLevelsList: updatedEducationLevelsList,
  };

  const personalFields = getPersonalFields(
    canEdit,
    personalConfig,
    formatMessage,
  );

  return [personalFields];
};

export const getPatientFieldsetList = ({
  profile,
  rawGenderList,
  rawMaritalStatusList,
  languageList,
  nameSuffixList,
  intl: { formatMessage },
  canEdit,
  canEditPatient,
  profileEditable,
  errors,
  rawRaceList,
  rawEthnicityList,
  rawGenderIdentityList,
  veteranStatusesList,
  educationLevelsList,
  ssnHidingEnabled,
}) => {
  const genderList = convertGenderList(rawGenderList, formatMessage);
  const maritalStatusList = convertMaritalList(
    rawMaritalStatusList,
    formatMessage,
  );

  const isEditable = canEdit || canEditPatient;

  const genderIdentityList = convertGenderIdentitiesList(rawGenderIdentityList);
  const updatedVeteranStatusesList = convertListWithName(veteranStatusesList);
  const updatedEducationLevelsList = convertListWithName(educationLevelsList);

  const personalConfig = {
    ...profile,
    profileEditable,
    genderList,
    maritalStatusList,
    languageList,
    nameSuffixList,
    isEditable,
    errors,
    rawRaceList,
    rawEthnicityList,
    genderIdentityList,
    veteranStatusesList: updatedVeteranStatusesList,
    educationLevelsList: updatedEducationLevelsList,
    ssnHidingEnabled,
  };

  const personalFields = getPersonalFields(
    isEditable,
    personalConfig,
    formatMessage,
  );

  return [personalFields];
};

export const getFieldsetDescriptor = ({
  email,
  phoneNumber,
  errors,
  intl: { formatMessage },
}) => {
  return [
    {
      title: formatMessage(PROFILE_FIELDS.listTitles.contacts),
      columns: true,
      errors,
      fields: [
        {
          name: formatMessage(PROFILE_FIELDS.fieldNames.mobilePhone),
          value: getPhoneWithoutUSCode(phoneNumber),
          fieldName: 'phoneNumber',
          type: 'mobilePhone',
          validationRules: {
            [RULES.PHONE]: {
              message: 'profile.phone.format',
            },
          },
          limit: LENGTH_VALUES.PHONE_NUMBER.MAX,
        },
        {
          name: formatMessage(PROFILE_FIELDS.fieldNames.email),
          value: email,
          fieldName: 'email',
          type: 'email',
          validationRules: {
            [RULES.EMAIL]: {
              message: 'profile.email.format',
            },
            [RULES.REQUIRED]: {
              message: 'profile.email.required',
            },
          },
        },
      ],
    },
  ];
};

export const getDocumentFieldset = (
  formatMessage,
  userDocuments,
  canEdit,
  optional,
) => {
  const getDocumentUrl = (key, param) => {
    const document = userDocuments.find(doc => doc.key === key);
    return get(document, param);
  };
  const extraTitle = optional ? formatMessage({ id: 'common.optional' }) : '';

  const frontCardUrl = getDocumentUrl(DOCS.CARD_1, 'attachment.thumbMiddle');
  const insuranceCardName = formatMessage(
    PROFILE_FIELDS.fieldNames.insuranceCard,
  );
  const backCardName = !frontCardUrl && !canEdit ? insuranceCardName : '\u00A0';
  return {
    title: formatMessage({ id: 'documentation.common.title' }),
    columns: true,
    fields: [
      {
        name: `${insuranceCardName} ${extraTitle}`,
        hideEllipsis: true,
        type: 'image',
        fieldName: DOCS.CARD_1,
        id: getDocumentUrl(DOCS.CARD_1, 'id'),
        limit: MAX_FILE_SIZE,
        value: '',
        src: frontCardUrl,
        text: `${formatMessage({
          id: 'community.newPost.addPhoto',
        })} (${formatMessage({ id: 'documentation.common.frontSide' })})`,
        disabledText: `${formatMessage({
          id: 'documentation.common.addPhoto',
        })} ${capitalize(
          formatMessage({
            id: 'documentation.common.frontSide',
          }),
        )}`,
      },
      {
        name: backCardName,
        type: 'image',
        fieldName: DOCS.CARD_2,
        id: getDocumentUrl(DOCS.CARD_2, 'id'),
        limit: MAX_FILE_SIZE,
        value: null,
        src: getDocumentUrl(DOCS.CARD_2, 'attachment.thumbMiddle'),
        text: `${formatMessage({
          id: 'community.newPost.addPhoto',
        })} (${formatMessage({ id: 'documentation.common.backSide' })})`,
        disabledText: `${formatMessage({
          id: 'documentation.common.addPhoto',
        })} ${capitalize(
          formatMessage({
            id: 'documentation.common.backSide',
          }),
        )}`,
      },
      {
        name: `${formatMessage(
          PROFILE_FIELDS.fieldNames.driverLicense,
        )} ${extraTitle}`,
        hideEllipsis: true,
        type: 'image',
        fieldName: DOCS.DRIVER_LICENCE,
        id: getDocumentUrl(DOCS.DRIVER_LICENCE, 'id'),
        limit: MAX_FILE_SIZE,
        value: null,
        src: getDocumentUrl(DOCS.DRIVER_LICENCE, 'attachment.thumbMiddle'),
        text: `${formatMessage({
          id: 'community.newPost.addPhoto',
        })}`,
        disabledText: `${formatMessage({
          id: 'documentation.common.addPhoto',
        })} ${formatMessage({ id: 'profile.fields.driverLicense' })}`,
      },
    ],
  };
};

export const getDriverLicenseDocumentFieldset = (
  formatMessage,
  userDocuments,
  isEditMode,
) => {
  const getDocumentUrl = (key, param) => {
    const document = userDocuments.find(doc => doc.key === key);
    return get(document, param);
  };
  const extraTitle = isEditMode ? formatMessage({ id: 'common.optional' }) : '';

  return {
    title: formatMessage({ id: 'documentation.common.title' }),
    columns: true,
    fields: [
      {
        name: `${formatMessage(
          PROFILE_FIELDS.fieldNames.driverLicense,
        )} ${extraTitle}`,
        hideEllipsis: true,
        type: 'image',
        fieldName: DOCS.DRIVER_LICENCE,
        id: getDocumentUrl(DOCS.DRIVER_LICENCE, 'id'),
        limit: MAX_FILE_SIZE,
        value: null,
        src: getDocumentUrl(DOCS.DRIVER_LICENCE, 'attachment.thumbMiddle'),
        text: `${formatMessage({
          id: 'community.newPost.addPhoto',
        })}`,
        disabledText: `${formatMessage({
          id: 'profile.fields.driverLicense',
        })}`,
      },
    ],
  };
};



// WEBPACK FOOTER //
// ./src/components/ProfileLayout/utils/getFieldset.js